import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Checkbox,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Icon,
    IconButton,
    ResponsiveDialog,
    Stack,
    styled,
    Tag,
    Text,
    TextArea,
} from 'shell/components';
import { COMMENTARY_ITEM_ID } from 'utils/feedbackRegeneration';

const GENERAL_OPTION_ID = 'general-feedback';
const DETAILED_OPTION_ID = 'detailed-feedback';

const FeedbackRegenerationDialog = ({
    assignment,
    feedback,
    open,
    pendingItemIds = [],
    suggestedItemIds = [],
    onClose,
    onRegenerate,
}) => {
    const { t } = useTranslation('assignment');
    const [selectedOptionIds, setSelectedOptionIds] = useState(new Set());
    const [customPrompt, setCustomPrompt] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    const isRubricFeedback = feedback?.feedback_type === 'rubric';
    const options = useMemo(() => {
        const pendingItemIdSet = new Set(pendingItemIds);
        const suggestedItemIdSet = new Set(suggestedItemIds);
        const withAvailability = (option) => ({
            ...option,
            availableItemIds: option.itemIds.filter((itemId) => !pendingItemIdSet.has(itemId)),
            suggested: option.itemIds.some((itemId) => suggestedItemIdSet.has(itemId)),
        });

        if (typeof feedback === 'string') {
            return [
                withAvailability({
                    id: GENERAL_OPTION_ID,
                    itemIds: [COMMENTARY_ITEM_ID],
                    label: t('grading-section.feedback-regeneration-dialog.general-feedback', 'General Feedback'),
                }),
            ];
        }
        if (!feedback || typeof feedback !== 'object') return [];

        const suggestions = (feedback.improvement_suggestions ?? []).filter(({ id }) => Boolean(id));
        if (isRubricFeedback) {
            return suggestions.map(({ criterion_id: criterionId, id }) =>
                withAvailability({
                    id,
                    itemIds: [id],
                    label:
                        assignment?.rubric?.criteria?.find((criterion) => criterion.id === criterionId)?.title ??
                        t('grading-section.feedback-regeneration-dialog.criterion-feedback', 'Criterion Feedback'),
                })
            );
        }

        const generalOption = Object.prototype.hasOwnProperty.call(feedback, 'commentary')
            ? [
                  withAvailability({
                      id: GENERAL_OPTION_ID,
                      itemIds: [COMMENTARY_ITEM_ID],
                      label: t('grading-section.feedback-regeneration-dialog.general-feedback', 'General Feedback'),
                  }),
              ]
            : [];
        const detailedItemIds = suggestions.map(({ id }) => id);
        const detailedOption = detailedItemIds.length
            ? [
                  withAvailability({
                      id: DETAILED_OPTION_ID,
                      itemIds: detailedItemIds,
                      label: t('grading-section.feedback-regeneration-dialog.detailed-feedback', 'Detailed Feedback'),
                  }),
              ]
            : [];

        return [...generalOption, ...detailedOption];
    }, [assignment?.rubric?.criteria, feedback, isRubricFeedback, pendingItemIds, suggestedItemIds, t]);

    const availableOptions = useMemo(
        () => options.filter(({ availableItemIds }) => availableItemIds.length),
        [options]
    );
    const defaultSelectedOptionIds = useMemo(() => {
        const suggestedOptionIds = availableOptions.filter(({ suggested }) => suggested).map(({ id }) => id);
        return suggestedOptionIds.length ? suggestedOptionIds : availableOptions.map(({ id }) => id);
    }, [availableOptions]);
    const selectedOptions = options.filter(({ id }) => selectedOptionIds.has(id));
    const selectedItemIds = selectedOptions.flatMap(({ availableItemIds }) => availableItemIds);
    const allSelected =
        Boolean(availableOptions.length) && availableOptions.every(({ id }) => selectedOptionIds.has(id));

    useEffect(() => {
        if (!open) return;

        setSelectedOptionIds(new Set(defaultSelectedOptionIds));
        setCustomPrompt('');
        setIsSubmitting(false);
    }, [defaultSelectedOptionIds, open]);

    const handleClose = () => {
        if (!isSubmitting) onClose();
    };

    const handleToggleOption = (option) => {
        if (!option.availableItemIds.length) return;
        setSelectedOptionIds((current) => {
            const next = new Set(current);
            if (next.has(option.id)) next.delete(option.id);
            else next.add(option.id);
            return next;
        });
    };

    const handleToggleAll = () => {
        setSelectedOptionIds(allSelected ? new Set() : new Set(availableOptions.map(({ id }) => id)));
    };

    const handleAddPrompt = (prompt) => {
        setCustomPrompt((current) => (current.trim() ? `${current.trim()}\n${prompt}` : prompt));
    };

    const handleRegenerate = async () => {
        if (!selectedItemIds.length) return;
        setIsSubmitting(true);
        let didStart = false;
        try {
            didStart = await onRegenerate({
                targetItemIds: selectedItemIds,
                customPrompt: customPrompt.trim(),
            });
        } finally {
            setIsSubmitting(false);
        }
        if (didStart) onClose();
    };

    const promptSuggestions = [
        t('grading-section.feedback-regeneration-dialog.more-encouraging', 'More encouraging'),
        t('grading-section.feedback-regeneration-dialog.more-specific', 'More specific'),
        t('grading-section.feedback-regeneration-dialog.shorter', 'Shorter'),
        t('grading-section.feedback-regeneration-dialog.more-detailed', 'More detailed'),
        t('grading-section.feedback-regeneration-dialog.more-actionable', 'More actionable'),
    ];

    return (
        <CustomResponsiveDialog
            aria-labelledby={'feedback-regeneration-dialog-title'}
            open={open}
            scroll={'paper'}
            onClose={handleClose}
        >
            <CustomDialogTitle>
                <Stack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                    <Text id={'feedback-regeneration-dialog-title'} component={'h1'} variant={'h6'} weight={'medium'}>
                        {t('grading-section.feedback-regeneration-dialog.title', 'Feedback Regeneration')}
                    </Text>
                    <IconButton
                        aria-label={t('common:controls.close', 'Close')}
                        disabled={isSubmitting}
                        kind={'exit'}
                        size={'medium'}
                        variant={'square'}
                        onClick={handleClose}
                    />
                </Stack>
            </CustomDialogTitle>
            <Divider />
            <CustomDialogContent>
                <Stack spacing={4}>
                    <SelectAllStack alignItems={'center'} direction={'row'} justifyContent={'space-between'}>
                        <Checkbox
                            aria-label={
                                isRubricFeedback
                                    ? t('grading-section.feedback-regeneration-dialog.select-all', 'Select All')
                                    : t(
                                          'grading-section.feedback-regeneration-dialog.select-all-feedback',
                                          'Select All Feedback'
                                      )
                            }
                            checked={allSelected}
                            disabled={!availableOptions.length}
                            label={
                                isRubricFeedback
                                    ? t('grading-section.feedback-regeneration-dialog.select-all', 'Select All')
                                    : t(
                                          'grading-section.feedback-regeneration-dialog.select-all-feedback',
                                          'Select All Feedback'
                                      )
                            }
                            onChange={handleToggleAll}
                        />
                        <Text color={'light'} variant={'p2'}>
                            {t(
                                'grading-section.feedback-regeneration-dialog.selection-count',
                                '{{selected}} of {{total}} selected',
                                { selected: selectedOptions.length, total: options.length }
                            )}
                        </Text>
                    </SelectAllStack>
                    <OptionsStack spacing={6}>
                        {options.map((option) => (
                            <OptionStack
                                key={option.id}
                                alignItems={'center'}
                                direction={'row'}
                                justifyContent={'space-between'}
                                spacing={3}
                            >
                                <Checkbox
                                    checked={selectedOptionIds.has(option.id)}
                                    disabled={!option.availableItemIds.length}
                                    label={option.label}
                                    noWrap={false}
                                    onChange={() => handleToggleOption(option)}
                                />
                                {option.suggested && <Tag type={'general'} value={'regenerationSuggested'} />}
                            </OptionStack>
                        ))}
                    </OptionsStack>
                    <Stack spacing={2}>
                        <Text variant={'p1'} weight={'medium'}>
                            {t(
                                'grading-section.feedback-regeneration-dialog.custom-prompt-label',
                                'Custom Prompt for Feedback Regeneration'
                            )}
                        </Text>
                        <Text color={'light'} variant={'p2'}>
                            {isRubricFeedback
                                ? t(
                                      'grading-section.feedback-regeneration-dialog.rubric-prompt-description',
                                      'Applies to all selected criteria for this submission.'
                                  )
                                : t(
                                      'grading-section.feedback-regeneration-dialog.feedback-prompt-description',
                                      'Applies to all selected feedback above, not just one.'
                                  )}
                        </Text>
                        <TextArea
                            minRows={4}
                            placeholder={t(
                                'grading-section.feedback-regeneration-dialog.custom-prompt-placeholder',
                                'List the items you would like the AI to modify based on your feedback in this submission.'
                            )}
                            value={customPrompt}
                            onChange={(event) => setCustomPrompt(event.target.value)}
                        />
                        <PromptStack direction={'row'} spacing={2}>
                            {promptSuggestions.map((prompt) => (
                                <Button
                                    key={prompt}
                                    color={'neutral'}
                                    size={'small'}
                                    startIcon={<Icon kind={'add'} />}
                                    variant={'secondary'}
                                    onClick={() => handleAddPrompt(prompt)}
                                >
                                    {prompt}
                                </Button>
                            ))}
                        </PromptStack>
                    </Stack>
                </Stack>
            </CustomDialogContent>
            <Divider />
            <CustomDialogActions>
                <Button color={'neutral'} disabled={isSubmitting} variant={'tertiary'} onClick={handleClose}>
                    {t('common:controls.cancel', 'Cancel')}
                </Button>
                <Button
                    disabled={!selectedItemIds.length}
                    loading={isSubmitting}
                    loadingGrow
                    onClick={handleRegenerate}
                >
                    {t('common:controls.regenerate', 'Regenerate')}
                </Button>
            </CustomDialogActions>
        </CustomResponsiveDialog>
    );
};

const CustomResponsiveDialog = styled(ResponsiveDialog)(({ theme }) => ({
    '& .MuiPaper-root > .MuiBox-root': {
        padding: 0,
    },
    '& .MuiDialog-paper': {
        width: '821px',
        maxWidth: 'calc(100% - 32px)',
        borderRadius: '16px',
    },
    '& .MuiDrawer-paper > .MuiBox-root': {
        width: '100vw',
        padding: 0,
    },
    '& .MuiDrawer-paper': {
        background: theme.palette.ensightful.shades[0],
    },
}));

const CustomDialogTitle = styled(DialogTitle)(({ theme }) => ({
    padding: theme.spacing(5),
}));

const CustomDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: theme.spacing(5),
}));

const CustomDialogActions = styled(DialogActions)(({ theme }) => ({
    gap: theme.spacing(4),
    padding: theme.spacing(4, 5),
}));

const SelectAllStack = styled(Stack)(({ theme }) => ({
    minHeight: '56px',
    padding: theme.spacing(2, 4),
    borderRadius: '16px',
    background: theme.palette.ensightful.neutral[50],
}));

const OptionsStack = styled(Stack)(({ theme }) => ({
    padding: theme.spacing(4),
    border: `1px solid ${theme.palette.ensightful.neutral[300]}`,
    borderRadius: '16px',
}));

const OptionStack = styled(Stack)(({ theme }) => ({
    flexWrap: 'wrap',
    rowGap: theme.spacing(2),
    '& .MuiFormControlLabel-root': {
        flex: '1 1 240px',
        minWidth: 0,
    },
}));

const PromptStack = styled(Stack)(() => ({
    flexWrap: 'wrap',
    rowGap: '8px',
}));

export default FeedbackRegenerationDialog;
